import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
  CircularProgress, IconButton, Tooltip, Typography, TextField,
  Popover, List, ListItemButton, ListItemIcon, ListItemText, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
} from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PublicIcon from '@mui/icons-material/Public';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import DeleteIcon from '@mui/icons-material/Delete';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { useMessage } from '../contexts/MessageContext';
import { useSystem } from '../contexts/SystemContext';
import { useLocation as useRouterLocation } from 'react-router-dom';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import GlobeView, { type BoundsInfo } from '../components/life-center/GlobeView';
import FeaturePanel from '../components/life-center/FeaturePanel';
import WeatherDetailCard from '../components/life-center/WeatherDetailCard';
import AttractionDetailCard from '../components/life-center/AttractionDetailCard';
import TripPanel from '../components/life-center/TripPanel';
import {
  fetchAllCitiesWeather,
  fetchLocationWeather,
  type CityWeather,
} from '../services/lifeCenterService';
import {
  fetchAttractionsByBounds,
  peekCachedAttractions,
  type Attraction,
} from '../services/attractionService';
import { type TripPlan, normalizeTrips } from '../services/tripService';
import {
  saveMyCities, loadLifeData, saveTripPlans,
  type SavedCity, type HealthRecord, type HealthData,
} from '../services/lifeDataService';
import {
  getMyLocations, createMyLocation, updateMyLocation, deleteMyLocation,
  type MyLocation,
} from '../services/eeMyLocationsService';
import HealthRecordDialog from '../components/health/HealthRecordDialog';
import HealthSummaryCard from '../components/health/HealthSummaryCard';

const LifeCenterPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const routerLocation = useRouterLocation();
  const flyToQuery = new URLSearchParams(routerLocation.search).get('q') ?? '';
  const pageRef = useRef<HTMLDivElement>(null);
  const { showApiError, showSuccess } = useMessage();

  const [features, setFeatures]           = useState<Record<string, boolean>>({ weather: false, tourism: false, trip: false, health: false, locations: false });
  const [weatherData, setWeatherData]     = useState<CityWeather[]>([]);
  const [loadingWeather, setLoadingWeather] = useState(false);
  const [selectedCity, setSelectedCity]   = useState<CityWeather | null>(null);
  const [isFullscreen, setIsFullscreen]   = useState(false);
  const [clickPos, setClickPos]           = useState<{ x: number; y: number } | null>(null);

  // ── My Cities ──────────────────────────────────────────────────────────────
  const [myCities, setMyCities] = useState<SavedCity[]>([]);
  const [showMyCities, setShowMyCities]         = useState(false);
  const [myCitiesWeather, setMyCitiesWeather]   = useState<CityWeather[]>([]);
  const [loadingMyCity, setLoadingMyCity]       = useState(false);

  // Context menu state: anchor position + pending lat/lng
  const [contextMenu, setContextMenu] = useState<{
    lat: number; lng: number; anchorX: number; anchorY: number;
  } | null>(null);

  const [manageDlgOpen, setManageDlgOpen] = useState(false);

  // ── Trip Plans ────────────────────────────────────────────────────────────
  const [tripPlans, setTripPlans]           = useState<TripPlan[]>([]);
  const [visiblePlanIds, setVisiblePlanIds] = useState<string[]>([]);

  const tripAttractions = useMemo(() => {
    const visible = tripPlans.filter(p => visiblePlanIds.includes(p.id));
    const seen = new Set<string>();
    const result: Attraction[] = [];
    visible.forEach(p =>
      p.attractions.forEach(a => {
        if (!seen.has(a.id)) { seen.add(a.id); result.push(a); }
      })
    );
    return result;
  }, [tripPlans, visiblePlanIds]);

  const handlePlansChange = useCallback((newPlans: TripPlan[]) => {
    setTripPlans(newPlans);
    saveTripPlans(newPlans).catch(() => {});
  }, []);

  // ── Health ────────────────────────────────────────────────────────────────
  const [healthData, setHealthData]                   = useState<HealthData>({ config: { types: [] }, records: [] });
  const [healthDlgOpen, setHealthDlgOpen]             = useState(false);
  const [healthDlgLocation, setHealthDlgLocation]     = useState<{ lat: number; lng: number; placeName: string; country: string; timezone: string; fromGps?: boolean } | undefined>(undefined);
  const [healthDlgTs, setHealthDlgTs]                 = useState<string | undefined>(undefined);
  const [healthSummaryRecord, setHealthSummaryRecord] = useState<HealthRecord | null>(null);
  const [healthSummaryPos, setHealthSummaryPos]       = useState<{ x: number; y: number } | null>(null);
  const [editHealthRecord, setEditHealthRecord]       = useState<HealthRecord | undefined>(undefined);
  const [geoLocating, setGeoLocating]                 = useState(false);
  const [geoPermDenied, setGeoPermDenied]             = useState(false);
  const [gpsFixedLocation, setGpsFixedLocation]       = useState<{ lat: number; lng: number; placeName: string; country: string; timezone: string } | null>(null);

  // ── My Locations ────────────────────────────────────────────────────────
  const [markingMode, setMarkingMode]       = useState(false);
  const [myLocations, setMyLocations]       = useState<MyLocation[]>([]);
  const [pendingLocPos, setPendingLocPos]   = useState<{ lat: number; lng: number } | null>(null);
  const [pendingCtxPos, setPendingCtxPos]   = useState<{ x: number; y: number } | null>(null);
  const [locNameDlgOpen, setLocNameDlgOpen] = useState(false);
  const [locNameValue, setLocNameValue]     = useState('');
  const [myLocInfoDlg, setMyLocInfoDlg]     = useState<{ loc: MyLocation; x: number; y: number } | null>(null);
  const [locEditDlgOpen, setLocEditDlgOpen] = useState(false);
  const [locEditValue, setLocEditValue]     = useState('');

  // 用 sys_timezone 算當月（對齊 record.ts 寫入時用的本地時區），
  // 避免凌晨 UTC 跨日把上月紀錄誤算進當月 filter。
  // E6b-c：租戶時區走公開的 tenantBranding（organizations.timezone）·不再依賴平台管理專用的 sysProfile
  const { tenantBranding } = useSystem();
  const tz = tenantBranding?.timezone || 'Asia/Taipei';
  const currentMonth = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
  }).format(new Date()); // "2026-06"
  const healthMarkersWithLocation = healthData.records.filter(
    r => !!r.location && r.ts.slice(0, 7) === currentMonth
  );

  // ── Tourism ────────────────────────────────────────────────────────────────
  const [attractionData, setAttractionData]           = useState<Attraction[]>([]);
  const [selectedAttraction, setSelectedAttraction]   = useState<Attraction | null>(null);
  const [attractionClickPos, setAttractionClickPos]   = useState<{ x: number; y: number } | null>(null);
  const [loadingAttractions, setLoadingAttractions]   = useState(false);
  const [attractionsError, setAttractionsError]       = useState(false);
  const [zoomTooLow, setZoomTooLow]                   = useState(false);
  const boundsDebounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Single source of truth: DB. Load once on mount, then write back via API only.
  const lifeDataReadyRef = useRef(false);
  useEffect(() => {
    loadLifeData().then(d => {
      lifeDataReadyRef.current = true;
      setMyCities(d.my_cities ?? []);
      setTripPlans(normalizeTrips(d.trip_plans ?? []));
      setHealthData(d.health);
    }).catch(e => { console.warn('[LifeCenter] loadLifeData failed:', e); showApiError(e); });
  }, []);

  useEffect(() => {
    getMyLocations().then(setMyLocations).catch(() => {});
  }, []);

  // Persist myCities to backend — guard prevents writing empty state before load completes
  useEffect(() => {
    if (!lifeDataReadyRef.current) return;
    saveMyCities(myCities)
      .then(() => showSuccess('SYS310011'))
      .catch(e => { console.warn('[LifeCenter] saveMyCities failed:', e); showApiError(e); });
  }, [myCities]);

  // When showMyCities is on, fetch weather for all saved cities whenever the list changes
  useEffect(() => {
    if (!showMyCities || myCities.length === 0) {
      if (showMyCities) setMyCitiesWeather([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingMyCity(true);
      try {
        const results = await Promise.all(
          myCities.map(c => fetchLocationWeather(c.lat, c.lng))
        );
        if (!cancelled) setMyCitiesWeather(results);
      } catch {
        // silently ignore
      } finally {
        if (!cancelled) setLoadingMyCity(false);
      }
    })();
    return () => { cancelled = true; };
  }, [showMyCities, myCities]);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const toContainerPos = useCallback((clientX: number, clientY: number) => {
    const rect = pageRef.current?.getBoundingClientRect();
    return { x: clientX - (rect?.left ?? 0), y: clientY - (rect?.top ?? 0) };
  }, []);

  // ── Fullscreen ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) pageRef.current?.requestFullscreen();
    else document.exitFullscreen();
  }, []);

  // ── Feature toggle ─────────────────────────────────────────────────────────
  const toggleFeature = useCallback(async (key: string) => {
    if (key === 'weather') {
      const next = !features.weather;
      setFeatures(f => ({ ...f, weather: next }));
      if (next && weatherData.length === 0) {
        setLoadingWeather(true);
        try {
          const data = await fetchAllCitiesWeather();
          setWeatherData(data);
        } finally {
          setLoadingWeather(false);
        }
      }
      if (!next) { setSelectedCity(null); setShowMyCities(false); }
    }
    if (key === 'tourism') {
      const next = !features.tourism;
      setFeatures(f => ({ ...f, tourism: next }));
      if (!next) { setAttractionData([]); setSelectedAttraction(null); }
    }
    if (key === 'trip') {
      if (visiblePlanIds.length > 0) {
        // Trips are active on the globe — clicking again turns them off
        setVisiblePlanIds([]);
        setFeatures(f => ({ ...f, trip: false }));
      } else {
        setFeatures(f => ({ ...f, trip: !f.trip }));
      }
    }
    if (key === 'health') {
      const next = !features.health;
      setFeatures(f => ({ ...f, health: next }));
      if (next) {
        loadLifeData().then(d => setHealthData(d.health)).catch(() => {});
      } else {
        setGpsFixedLocation(null);
      }
    }
    if (key === 'locations') {
      const next = !features.locations;
      setFeatures(f => ({ ...f, locations: next }));
      if (!next) setMarkingMode(false);
    }
  }, [features.weather, features.tourism, features.health, features.locations, weatherData.length, visiblePlanIds]);

  // ── Shared location resolver (Nominatim + Open-Meteo timezone) ────────────
  const resolveLocationData = useCallback(async (lat: number, lng: number) => {
    let timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    let placeName = `${lat.toFixed(2)}, ${lng.toFixed(2)}`;
    let country = '';
    try {
      const [meteoRes, nomRes] = await Promise.allSettled([
        fetch(`https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}&timezone=auto&forecast_days=1&hourly=temperature_2m`)
          .then(r => r.json()),
        fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`, {
          headers: { 'User-Agent': 'ap-base-ee-life-center/1.0', 'Accept-Language': 'zh-TW,en' },
        }).then(r => r.json()),
      ]);
      if (meteoRes.status === 'fulfilled' && meteoRes.value?.timezone) timezone = meteoRes.value.timezone;
      if (nomRes.status === 'fulfilled' && nomRes.value?.address) {
        const addr = nomRes.value.address;
        placeName = addr.city || addr.town || addr.village || addr.county || placeName;
        country = addr.country || '';
      }
    } catch { /* use defaults */ } // silent ok: Nominatim/Open-Meteo geocode fallback, non-blocking
    const localTs = new Intl.DateTimeFormat('sv-SE', {
      timeZone: timezone, dateStyle: 'short', timeStyle: 'short',
    }).format(new Date()).replace(' ', 'T').slice(0, 16);
    return { lat, lng, placeName, country, timezone, localTs };
  }, []);

  // ── Health location click (globe click in health mode) ────────────────────
  const handleHealthLocationClick = useCallback(async (lat: number, lng: number, _clientX: number, _clientY: number) => {
    const { placeName, country, timezone, localTs } = await resolveLocationData(lat, lng);
    setHealthDlgLocation({ lat, lng, placeName, country, timezone });
    setHealthDlgTs(localTs);
    setEditHealthRecord(undefined);
    setHealthDlgOpen(true);
  }, [resolveLocationData]);

  // ── Record at GPS position (health indicator "My Location" button) ─────────
  const handleRecordAtMyLocation = useCallback(async () => {
    if (!navigator.geolocation) return;
    setGeoLocating(true);
    try {
      const pos = await new Promise<GeolocationPosition>((resolve, reject) =>
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          timeout: 10000,
          enableHighAccuracy: true,
          maximumAge: 0,   // 不吃 IP-based cache，強制重抓真實 GPS
        })
      );
      const { latitude: lat, longitude: lng } = pos.coords;
      const { placeName, country, timezone, localTs } = await resolveLocationData(lat, lng);
      const loc = { lat, lng, placeName, country, timezone };
      setGpsFixedLocation(loc);
      setHealthDlgLocation({ ...loc, fromGps: true });
      setHealthDlgTs(localTs);
      setEditHealthRecord(undefined);
      setHealthDlgOpen(true);
      setGeoPermDenied(false);
    } catch (err: unknown) {
      const geoErr = err as GeolocationPositionError;
      if (geoErr?.code === 1 /* PERMISSION_DENIED */) setGeoPermDenied(true);
    } finally {
      setGeoLocating(false);
    }
  }, [resolveLocationData]);

  const handleHealthMarkerClick = useCallback((record: HealthRecord, clientX: number, clientY: number) => {
    setHealthSummaryRecord(record);
    setHealthSummaryPos(toContainerPos(clientX, clientY));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── My Locations handlers ──────────────────────────────────────────────────
  const handlePendingMarkerContextMenu = useCallback((lat: number, lng: number, x: number, y: number) => {
    setPendingLocPos({ lat, lng });
    setPendingCtxPos({ x, y });
  }, []);

  const handleSaveLocation = useCallback(async () => {
    if (!pendingLocPos || !locNameValue.trim()) return;
    const newLoc = await createMyLocation({ name: locNameValue.trim(), lat: pendingLocPos.lat, lng: pendingLocPos.lng });
    setMyLocations(prev => [...prev, newLoc]);
    setLocNameDlgOpen(false);
    setLocNameValue('');
    setPendingLocPos(null);
    setPendingCtxPos(null);
    setMarkingMode(false);
  }, [pendingLocPos, locNameValue]);

  const handleMyLocationClick = useCallback((loc: MyLocation, x: number, y: number) => {
    setMyLocInfoDlg({ loc, x, y });
  }, []);

  const handleDeleteMyLocation = useCallback(async (id: number) => {
    await deleteMyLocation(id);
    setMyLocations(prev => prev.filter(l => l.id !== id));
    setMyLocInfoDlg(null);
  }, []);

  const handleEditMyLocation = useCallback(async () => {
    if (!myLocInfoDlg || !locEditValue.trim()) return;
    await updateMyLocation(myLocInfoDlg.loc.id, { name: locEditValue.trim() });
    setMyLocations(prev => prev.map(l => l.id === myLocInfoDlg.loc.id ? { ...l, name: locEditValue.trim() } : l));
    setLocEditDlgOpen(false);
    setMyLocInfoDlg(null);
  }, [myLocInfoDlg, locEditValue]);

  // ── City / location click ──────────────────────────────────────────────────
  const handleCityClick = useCallback((city: CityWeather, clientX: number, clientY: number) => {
    setClickPos(toContainerPos(clientX, clientY));
    setSelectedCity(prev => prev?.name === city.name ? null : city);
  }, [toContainerPos]);

  const handleLocationClick = useCallback(async (lat: number, lng: number, clientX: number, clientY: number) => {
    setClickPos(toContainerPos(clientX, clientY));
    try {
      const data = await fetchLocationWeather(lat, lng);
      setSelectedCity(data);
    } catch {
      // silently ignore fetch errors
    }
  }, [toContainerPos]);

  // ── Context menu ───────────────────────────────────────────────────────────
  const handleContextMenu = useCallback((lat: number, lng: number, clientX: number, clientY: number) => {
    setContextMenu({ lat, lng, anchorX: clientX, anchorY: clientY });
  }, []);

  const handleAttractionClick = useCallback((attr: Attraction, clientX: number, clientY: number) => {
    setAttractionClickPos(toContainerPos(clientX, clientY));
    setSelectedAttraction(prev => prev?.id === attr.id ? null : attr);
  }, [toContainerPos]);

  const handleBoundsChange = useCallback(({ north, south, east, west, zoom }: BoundsInfo) => {
    if (!features.tourism) return;
    clearTimeout(boundsDebounceRef.current);
    if (zoom < 5) {
      setAttractionData([]);
      setZoomTooLow(true);
      return;
    }
    setZoomTooLow(false);

    // Cache hit → display immediately, no debounce needed
    const immediate = peekCachedAttractions(north, south, east, west);
    if (immediate) { setAttractionData(immediate); return; }

    // Cache miss → debounce then fetch
    boundsDebounceRef.current = setTimeout(async () => {
      setLoadingAttractions(true);
      setAttractionsError(false);
      try {
        const data = await fetchAttractionsByBounds(north, south, east, west);
        setAttractionData(data);
      } catch (err) {
        console.warn('[LifeCenter] Overpass failed:', err);
        setAttractionsError(true);
      } finally {
        setLoadingAttractions(false);
      }
    }, 600);
  }, [features.tourism]);

  const isCurrentCityInMyList = selectedCity
    ? myCities.some(c => Math.abs(c.lat - selectedCity.lat) < 0.1 && Math.abs(c.lng - selectedCity.lng) < 0.1)
    : false;

  const handleToggleMyCity = useCallback(() => {
    if (!selectedCity) return;
    const exists = myCities.some(c =>
      Math.abs(c.lat - selectedCity.lat) < 0.1 && Math.abs(c.lng - selectedCity.lng) < 0.1);
    if (exists) {
      setMyCities(prev => prev.filter(c =>
        !(Math.abs(c.lat - selectedCity.lat) < 0.1 && Math.abs(c.lng - selectedCity.lng) < 0.1)));
    } else {
      setMyCities(prev => [...prev, {
        name:   selectedCity.name,
        nameEn: selectedCity.nameEn || selectedCity.name,
        lat:    selectedCity.lat,
        lng:    selectedCity.lng,
      }]);
    }
  }, [selectedCity, myCities]);

  const handleToggleShowMyCities = useCallback(() => {
    setContextMenu(null);
    setShowMyCities(prev => !prev);
  }, []);

  const handleOpenManage = useCallback(() => {
    setContextMenu(null);
    setManageDlgOpen(true);
  }, []);

  const handleDeleteCity = useCallback((index: number) => {
    setMyCities(prev => {
      const next = prev.filter((_, i) => i !== index);
      if (next.length === 0) setShowMyCities(false);
      return next;
    });
  }, []);

  // Displayed weather on the globe
  const displayedWeather = showMyCities ? myCitiesWeather : weatherData;
  const showWeatherMarkers = features.weather || showMyCities;

  return (
    <div
      ref={pageRef}
      style={{ position: 'relative', width: '100%', height: '100vh', overflow: 'hidden', background: '#050a18' }}
      onContextMenu={e => e.preventDefault()}
    >
      <GlobeView
        weatherData={displayedWeather}
        showWeather={showWeatherMarkers}
        lang={i18n.language}
        onCityClick={handleCityClick}
        onLocationClick={features.weather && !features.health ? handleLocationClick : undefined}
        onContextMenu={features.weather ? handleContextMenu : undefined}
        showTourism={features.tourism}
        attractionData={attractionData}
        onAttractionClick={handleAttractionClick}
        onBoundsChange={handleBoundsChange}
        tripAttractions={tripAttractions}
        onTripAttractionClick={handleAttractionClick}
        showHealth={features.health}
        healthMarkers={healthMarkersWithLocation}
        onHealthLocationClick={features.health ? handleHealthLocationClick : undefined}
        onHealthMarkerClick={features.health ? handleHealthMarkerClick : undefined}
        flyToQuery={flyToQuery || undefined}
        markingMode={markingMode}
        myLocations={features.locations ? myLocations : []}
        onPendingMarkerContextMenu={handlePendingMarkerContextMenu}
        onMyLocationClick={handleMyLocationClick}
        isFullscreen={isFullscreen}
        onToggleFullscreen={toggleFullscreen}
      />

      <FeaturePanel
        active={{ ...features, trip: visiblePlanIds.length > 0 }}
        onToggle={toggleFeature}
        markingMode={markingMode}
        onToggleMarking={() => setMarkingMode(m => !m)}
      />

      {features.trip && (
        <TripPanel
          plans={tripPlans}
          visiblePlanIds={visiblePlanIds}
          lang={i18n.language}
          onPlansChange={handlePlansChange}
          onVisibilityChange={setVisiblePlanIds}
          onClose={() => setFeatures(f => ({ ...f, trip: false }))}
        />
      )}

      {(loadingWeather || loadingMyCity) && (
        <div style={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%,-50%)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
        }}>
          <CircularProgress sx={{ color: '#4285f4' }} />
          <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 13 }}>
            {t('lifeCenter.loading')}
          </Typography>
        </div>
      )}

      <WeatherDetailCard
        city={selectedCity}
        position={clickPos}
        onClose={() => { setSelectedCity(null); setClickPos(null); }}
        isInMyCity={isCurrentCityInMyList}
        onToggleMyCity={handleToggleMyCity}
      />

      <AttractionDetailCard
        attraction={selectedAttraction}
        position={attractionClickPos}
        onClose={() => { setSelectedAttraction(null); setAttractionClickPos(null); }}
        container={pageRef.current}
        plans={tripPlans}
        onPlansChange={handlePlansChange}
        onOpenTripPanel={() => setFeatures(f => ({ ...f, trip: true }))}
      />

      {/* Attraction loading */}
      {loadingAttractions && (
        <div style={{
          position: 'absolute', bottom: 56, left: '50%', transform: 'translateX(-50%)',
          display: 'flex', alignItems: 'center', gap: 8, zIndex: 10,
          background: 'rgba(15,25,50,0.85)', backdropFilter: 'blur(6px)',
          border: '1px solid rgba(255,140,66,0.4)', borderRadius: 8, padding: '6px 14px',
        }}>
          <CircularProgress size={14} sx={{ color: '#ff8c42' }} />
          <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: 12 }}>
            {t('lifeCenter.attractionsLoading')}
          </Typography>
        </div>
      )}

      {/* Attraction fetch error */}
      {attractionsError && !loadingAttractions && (
        <div style={{
          position: 'absolute', bottom: 56, left: '50%', transform: 'translateX(-50%)',
          display: 'flex', alignItems: 'center', gap: 8, zIndex: 10,
          background: 'rgba(15,25,50,0.92)', backdropFilter: 'blur(6px)',
          border: '1px solid rgba(255,80,80,0.55)', borderRadius: 8, padding: '6px 14px',
          cursor: 'pointer',
        }}
          onClick={() => {
            setAttractionsError(false);
            setAttractionData([]);
            setFeatures(f => ({ ...f, tourism: false }));
            setTimeout(() => setFeatures(f => ({ ...f, tourism: true })), 80);
          }}
        >
          <Typography sx={{ color: 'rgba(255,160,160,0.95)', fontSize: 12 }}>
            ⚠️ {i18n.language.startsWith('en') ? 'Attraction query failed — click to retry' : '景點查詢失敗，點此重試'}
          </Typography>
        </div>
      )}

      {/* Zoom hint when tourism is on but zoom too low */}
      {features.tourism && zoomTooLow && !loadingAttractions && (
        <div style={{
          position: 'absolute', bottom: 56, left: '50%', transform: 'translateX(-50%)',
          zIndex: 10, background: 'rgba(15,25,50,0.85)', backdropFilter: 'blur(6px)',
          border: '1px solid rgba(255,140,66,0.35)', borderRadius: 8, padding: '6px 14px',
        }}>
          <Typography sx={{ color: 'rgba(255,200,100,0.9)', fontSize: 12 }}>
            🔍 {t('lifeCenter.attractionsZoomIn')}
          </Typography>
        </div>
      )}

      {/* "顯示我的城市" active indicator */}
      {showMyCities && (
        <div style={{
          position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)',
          background: 'rgba(66,133,244,0.28)', border: '1px solid rgba(66,133,244,0.7)',
          borderRadius: 8, padding: '4px 14px', zIndex: 10,
          display: 'flex', alignItems: 'center', gap: 6,
          backdropFilter: 'blur(6px)',
        }}>
          <LocationOnIcon sx={{ color: '#7ec8ff', fontSize: 16 }} />
          <Typography sx={{ color: '#7ec8ff', fontSize: 12, fontWeight: 600 }}>
            {t('lifeCenter.myCitiesCount', { count: myCities.length })}
          </Typography>
          <span
            onClick={() => setShowMyCities(false)}
            style={{ color: 'rgba(255,255,255,0.5)', cursor: 'pointer', fontSize: 13, marginLeft: 4 }}
          >✕</span>
        </div>
      )}

      {/* Right-click context menu */}
      <Popover
        open={!!contextMenu}
        onClose={() => setContextMenu(null)}
        container={pageRef.current}
        anchorReference="anchorPosition"
        anchorPosition={contextMenu ? { top: contextMenu.anchorY, left: contextMenu.anchorX } : undefined}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            background: 'rgba(15,25,50,0.95)',
            border: '1px solid rgba(66,133,244,0.4)',
            backdropFilter: 'blur(10px)',
            borderRadius: 2,
            minWidth: 190,
            color: '#fff',
          },
        }}
      >
        <List dense disablePadding>
          <ListItemButton onClick={handleToggleShowMyCities} sx={{ '&:hover': { background: 'rgba(66,133,244,0.2)' } }}>
            <ListItemIcon sx={{ minWidth: 34, color: showMyCities ? '#4285f4' : '#7ec8ff' }}>
              <PublicIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={
                showMyCities
                  ? t('lifeCenter.showAllCities')
                  : t('lifeCenter.showMyCities')
              }
              primaryTypographyProps={{ fontSize: 13, color: showMyCities ? '#7ec8ff' : undefined }}
            />
            {myCities.length > 0 && (
              <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', pr: 1 }}>
                {myCities.length}
              </Typography>
            )}
          </ListItemButton>
          {myCities.length > 0 && (
            <>
              <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
              <ListItemButton onClick={handleOpenManage} sx={{ '&:hover': { background: 'rgba(66,133,244,0.2)' } }}>
                <ListItemIcon sx={{ minWidth: 34, color: '#7ec8ff' }}>
                  <ManageAccountsIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={t('lifeCenter.manageMyCities')}
                  primaryTypographyProps={{ fontSize: 13 }}
                />
              </ListItemButton>
            </>
          )}
        </List>
      </Popover>

      {/* Manage My Cities dialog */}
      <Dialog
        open={manageDlgOpen}
        onClose={() => setManageDlgOpen(false)}
        container={pageRef.current}
        PaperProps={{
          sx: {
            background: 'rgba(15,25,50,0.97)',
            border: '1px solid rgba(66,133,244,0.4)',
            backdropFilter: 'blur(12px)',
            borderRadius: 3,
            minWidth: 300,
            color: '#fff',
          },
        }}
      >
        <DialogTitle sx={{ fontSize: 15, fontWeight: 700, pb: 1, color: '#7ec8ff' }}>
          <LocationOnIcon sx={{ fontSize: 18, mr: 0.8, verticalAlign: 'middle' }} />
          {t('lifeCenter.myCitiesTitle')}
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: 'rgba(255,255,255,0.1)', p: 0 }}>
          {myCities.length === 0 ? (
            <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: 13, p: 2, textAlign: 'center' }}>
              {t('lifeCenter.myCitiesEmpty')}
            </Typography>
          ) : (
            <List dense disablePadding>
              {myCities.map((city, idx) => (
                <React.Fragment key={`${city.lat}-${city.lng}`}>
                  {idx > 0 && <Divider sx={{ borderColor: 'rgba(255,255,255,0.07)' }} />}
                  <ListItemButton
                    disableRipple
                    sx={{ cursor: 'default', '&:hover': { background: 'rgba(66,133,244,0.08)' } }}
                  >
                    <ListItemIcon sx={{ minWidth: 32, color: '#4285f4' }}>
                      <LocationOnIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={i18n.language === 'en' ? (city.nameEn || city.name) : city.name}
                      secondary={`${city.lat.toFixed(2)}, ${city.lng.toFixed(2)}`}
                      primaryTypographyProps={{ fontSize: 13, color: '#fff' }}
                      secondaryTypographyProps={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteCity(idx)}
                      sx={{ color: 'rgba(255,100,100,0.7)', '&:hover': { color: '#ff6b6b', background: 'rgba(255,100,100,0.12)' } }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ListItemButton>
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid rgba(255,255,255,0.1)', px: 2, py: 1 }}>
          {myCities.length > 0 && (
            <Button
              size="small"
              color="error"
              onClick={() => { setMyCities([]); setShowMyCities(false); }}
              sx={{ fontSize: 12, color: 'rgba(255,100,100,0.75)', mr: 'auto' }}
            >
              {t('lifeCenter.clearAll')}
            </Button>
          )}
          <Button
            size="small"
            onClick={() => setManageDlgOpen(false)}
            sx={{ fontSize: 12, color: 'rgba(255,255,255,0.6)' }}
          >
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Health mode indicator */}
      {features.health && (
        <div style={{
          position: 'absolute', bottom: 60, left: '50%', transform: 'translateX(-50%)',
          background: 'rgba(233,30,99,0.22)', border: '1px solid rgba(233,30,99,0.65)',
          borderRadius: 8, padding: '4px 10px 4px 14px', zIndex: 10,
          display: 'flex', alignItems: 'center', gap: 6, backdropFilter: 'blur(6px)',
        }}>
          <span style={{ fontSize: 14 }}>🩺</span>
          <Typography sx={{ color: '#f48fb1', fontSize: 12, fontWeight: 600 }}>
            {t('lifeCenter.clickGlobeToRecord')}
          </Typography>
          <div style={{ width: 1, height: 16, background: 'rgba(233,30,99,0.45)', margin: '0 2px' }} />
          <Tooltip title={geoPermDenied ? t('lifeCenter.gpsDenied') : t('lifeCenter.recordAtGps')}>
            <span>
              <IconButton
                size="small"
                onClick={handleRecordAtMyLocation}
                disabled={geoLocating || geoPermDenied}
                sx={{
                  color: geoPermDenied    ? 'rgba(255,130,130,0.45)' :
                         gpsFixedLocation ? '#e91e63' : '#f48fb1',
                  p: 0.4,
                  background: gpsFixedLocation ? 'rgba(233,30,99,0.22)' : 'transparent',
                  border: gpsFixedLocation ? '1px solid rgba(233,30,99,0.6)' : '1px solid transparent',
                  borderRadius: 1,
                  '&:hover:not(:disabled)': { color: '#e91e63', background: 'rgba(233,30,99,0.15)' },
                  '&.Mui-disabled': { color: 'rgba(255,130,130,0.35)' },
                }}
              >
                {geoLocating
                  ? <CircularProgress size={15} sx={{ color: '#f48fb1' }} />
                  : gpsFixedLocation
                    ? <GpsFixedIcon sx={{ fontSize: 17 }} />
                    : <MyLocationIcon sx={{ fontSize: 17 }} />}
              </IconButton>
            </span>
          </Tooltip>
          {gpsFixedLocation && !geoLocating && (
            <Tooltip title={t('lifeCenter.cancelGps')}>
              <IconButton
                size="small"
                onClick={() => setGpsFixedLocation(null)}
                sx={{
                  color: 'rgba(248,135,177,0.7)',
                  p: 0.3,
                  '&:hover': { color: '#f48fb1', background: 'rgba(233,30,99,0.15)' },
                }}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
        </div>
      )}

      {/* Health summary card (globe marker click) */}
      {healthSummaryRecord && healthSummaryPos && (
        <HealthSummaryCard
          record={healthSummaryRecord}
          healthConfig={healthData.config}
          position={healthSummaryPos}
          onClose={() => { setHealthSummaryRecord(null); setHealthSummaryPos(null); }}
          onEdit={r => {
            setEditHealthRecord(r);
            setHealthDlgLocation(undefined);
            setHealthDlgTs(undefined);
            setHealthDlgOpen(true);
            setHealthSummaryRecord(null);
            setHealthSummaryPos(null);
          }}
        />
      )}

      {/* Health record dialog */}
      <HealthRecordDialog
        open={healthDlgOpen}
        editRecord={editHealthRecord}
        healthConfig={healthData.config}
        prefillLocation={healthDlgLocation}
        prefillTs={healthDlgTs}
        container={pageRef.current}
        onClose={() => setHealthDlgOpen(false)}
        onSaved={() => {
          setHealthDlgOpen(false);
          loadLifeData().then(d => setHealthData(d.health)).catch(() => {});
        }}
      />

      {/* Marking mode hint */}
      {markingMode && (
        <div style={{
          position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)',
          background: 'rgba(255,111,0,0.18)', border: '1px solid rgba(255,111,0,0.6)',
          borderRadius: 8, padding: '4px 14px', zIndex: 10,
          display: 'flex', alignItems: 'center', gap: 6, backdropFilter: 'blur(6px)',
        }}>
          <BookmarkAddIcon sx={{ color: '#ff6f00', fontSize: 15 }} />
          <Typography sx={{ color: '#ffcc80', fontSize: 12, fontWeight: 600 }}>
            標記模式：點擊地球放置標記，再右鍵設為我的地點
          </Typography>
          <span onClick={() => setMarkingMode(false)} style={{ color: 'rgba(255,200,100,0.6)', cursor: 'pointer', fontSize: 13, marginLeft: 4 }}>✕</span>
        </div>
      )}

      {/* Pending marker context menu */}
      <Popover
        open={!!pendingCtxPos}
        onClose={() => setPendingCtxPos(null)}
        container={pageRef.current}
        anchorReference="anchorPosition"
        anchorPosition={pendingCtxPos ? { top: pendingCtxPos.y, left: pendingCtxPos.x } : undefined}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { background: 'rgba(15,25,50,0.95)', border: '1px solid rgba(255,111,0,0.4)', backdropFilter: 'blur(10px)', borderRadius: 2, minWidth: 160, color: '#fff' } }}
      >
        <List dense disablePadding>
          <ListItemButton
            onClick={() => { setPendingCtxPos(null); setLocNameValue(''); setLocNameDlgOpen(true); }}
            sx={{ '&:hover': { background: 'rgba(255,111,0,0.2)' } }}
          >
            <ListItemIcon sx={{ minWidth: 32, color: '#ff6f00' }}><BookmarkAddIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="設為我的地點" primaryTypographyProps={{ fontSize: 13 }} />
          </ListItemButton>
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
          <ListItemButton onClick={() => { setPendingCtxPos(null); setPendingLocPos(null); }} sx={{ '&:hover': { background: 'rgba(255,100,100,0.15)' } }}>
            <ListItemIcon sx={{ minWidth: 32, color: 'rgba(255,120,120,0.8)' }}><DeleteIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="移除標記" primaryTypographyProps={{ fontSize: 13, color: 'rgba(255,160,160,0.9)' }} />
          </ListItemButton>
        </List>
      </Popover>

      {/* Location name dialog */}
      <Dialog
        open={locNameDlgOpen}
        onClose={() => setLocNameDlgOpen(false)}
        container={pageRef.current}
        PaperProps={{ sx: { background: 'rgba(15,25,50,0.97)', border: '1px solid rgba(255,111,0,0.4)', backdropFilter: 'blur(12px)', borderRadius: 3, minWidth: 300, color: '#fff' } }}
      >
        <DialogTitle sx={{ fontSize: 15, fontWeight: 700, pb: 1, color: '#ffcc80' }}>
          <BookmarkAddIcon sx={{ fontSize: 18, mr: 0.8, verticalAlign: 'middle', color: '#ff6f00' }} />
          設為我的地點
        </DialogTitle>
        <DialogContent>
          {pendingLocPos && (
            <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', mb: 1 }}>
              {pendingLocPos.lat.toFixed(5)}, {pendingLocPos.lng.toFixed(5)}
            </Typography>
          )}
          <TextField
            autoFocus
            fullWidth size="small" label="地點名稱"
            value={locNameValue}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLocNameValue(e.target.value)}
            onKeyDown={(e: React.KeyboardEvent) => e.key === 'Enter' && handleSaveLocation()}
            InputLabelProps={{ sx: { color: 'rgba(255,255,255,0.5)' } }}
            sx={{ '& .MuiOutlinedInput-root': { color: '#fff', '& fieldset': { borderColor: 'rgba(255,111,0,0.4)' }, '&:hover fieldset': { borderColor: '#ff6f00' } } }}
          />
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setLocNameDlgOpen(false)} sx={{ color: 'rgba(255,255,255,0.5)' }}>取消</Button>
          <Button size="small" variant="contained" disabled={!locNameValue.trim()} onClick={handleSaveLocation}
            sx={{ background: '#ff6f00', '&:hover': { background: '#e65100' } }}>儲存</Button>
        </DialogActions>
      </Dialog>

      {/* My-location info popup */}
      {myLocInfoDlg && (
        <div
          style={{
            position: 'absolute',
            top: myLocInfoDlg.y - (pageRef.current?.getBoundingClientRect().top ?? 0) + 8,
            left: myLocInfoDlg.x - (pageRef.current?.getBoundingClientRect().left ?? 0) + 8,
            zIndex: 20,
            background: 'rgba(15,25,50,0.96)',
            border: '1px solid rgba(21,101,192,0.6)',
            borderRadius: 8,
            padding: '10px 14px',
            minWidth: 180,
            backdropFilter: 'blur(10px)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <Typography sx={{ color: '#7ec8ff', fontWeight: 700, fontSize: 14 }}>
              📍 {myLocInfoDlg.loc.name}
            </Typography>
            <span onClick={() => setMyLocInfoDlg(null)} style={{ color: 'rgba(255,255,255,0.4)', cursor: 'pointer', fontSize: 14, marginLeft: 8 }}>✕</span>
          </div>
          <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, mb: 1.5 }}>
            {myLocInfoDlg.loc.lat.toFixed(5)}, {myLocInfoDlg.loc.lng.toFixed(5)}
          </Typography>
          <div style={{ display: 'flex', gap: 6 }}>
            <Button size="small" variant="outlined"
              onClick={() => { setLocEditValue(myLocInfoDlg.loc.name); setLocEditDlgOpen(true); }}
              sx={{ fontSize: 11, color: '#7ec8ff', borderColor: 'rgba(21,101,192,0.5)', '&:hover': { borderColor: '#1565c0' } }}>
              重命名
            </Button>
            <Button size="small" color="error" variant="outlined"
              onClick={() => handleDeleteMyLocation(myLocInfoDlg.loc.id)}
              sx={{ fontSize: 11, borderColor: 'rgba(255,100,100,0.4)', color: 'rgba(255,130,130,0.8)', '&:hover': { borderColor: '#ff5252' } }}>
              刪除
            </Button>
          </div>
        </div>
      )}

      {/* My-location rename dialog */}
      <Dialog
        open={locEditDlgOpen}
        onClose={() => setLocEditDlgOpen(false)}
        container={pageRef.current}
        PaperProps={{ sx: { background: 'rgba(15,25,50,0.97)', border: '1px solid rgba(21,101,192,0.4)', backdropFilter: 'blur(12px)', borderRadius: 3, minWidth: 280, color: '#fff' } }}
      >
        <DialogTitle sx={{ fontSize: 14, fontWeight: 700, pb: 1, color: '#7ec8ff' }}>重命名地點</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus fullWidth size="small" label="地點名稱"
            value={locEditValue}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLocEditValue(e.target.value)}
            onKeyDown={(e: React.KeyboardEvent) => e.key === 'Enter' && handleEditMyLocation()}
            InputLabelProps={{ sx: { color: 'rgba(255,255,255,0.5)' } }}
            sx={{ '& .MuiOutlinedInput-root': { color: '#fff', '& fieldset': { borderColor: 'rgba(21,101,192,0.4)' }, '&:hover fieldset': { borderColor: '#1565c0' } } }}
          />
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setLocEditDlgOpen(false)} sx={{ color: 'rgba(255,255,255,0.5)' }}>取消</Button>
          <Button size="small" variant="contained" disabled={!locEditValue.trim()} onClick={handleEditMyLocation}
            sx={{ background: '#1565c0', '&:hover': { background: '#0d47a1' } }}>儲存</Button>
        </DialogActions>
      </Dialog>

      {/* Fullscreen toggle is now rendered inside GlobeView (top-right) */}
    </div>
  );
};

export default LifeCenterPage;
